import {
  CheckCircle,
  DollarSign,
  Users,
  Zap,
  Globe,
  TrendingUp,
  Shield,
  ChevronRight,
  Star,
} from 'lucide-react';

interface DealerLandingPageProps {
  onGetStarted: () => void;
}

export function DealerLandingPage({ onGetStarted }: DealerLandingPageProps) {
  return (
    <div className="min-h-screen bg-white">

      {/* Hero */}
      <section className="relative overflow-hidden text-white">
        {/* Dark car background */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=1600&q=80')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-950/90 via-gray-900/75 to-gray-900/40" />

        <div className="relative container mx-auto px-4 py-24 max-w-5xl">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="flex-1">
              {/* FAM logo mark */}
              <div className="flex items-center gap-2 mb-8">
                <div className="w-10 h-10 bg-gradient-to-br from-rose-500 to-amber-500 rounded-xl flex items-center justify-center shadow-lg">
                  <Globe className="w-5 h-5 text-white" />
                </div>
                <span className="text-white font-bold text-lg tracking-wide">FAM · Dealer Network</span>
              </div>

              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur rounded-full px-4 py-1.5 text-sm font-medium text-white/80 mb-6 border border-white/20">
                <Zap className="w-4 h-4 text-amber-400" />
                Free to list · No hidden fees
              </div>
              <h1 className="text-4xl sm:text-5xl font-extrabold leading-tight mb-5">
                Turn your lot into a<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-rose-400 to-amber-400">
                  funding machine
                </span>
              </h1>
              <p className="text-lg text-white/70 mb-8 leading-relaxed max-w-lg">
                List your inventory on FAM — the community-funded car platform. Buyers come pre-approved,
                backed by their own friends and family. You get full cash offers, fast.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={onGetStarted}
                  className="flex items-center justify-center gap-2 bg-gradient-to-r from-rose-500 to-amber-500 hover:from-rose-400 hover:to-amber-400 text-white font-bold px-8 py-4 rounded-full text-base shadow-lg shadow-rose-500/30 transition-all"
                >
                  List My Inventory Free
                  <ChevronRight className="w-5 h-5" />
                </button>
                <a
                  href="/campaigns"
                  className="flex items-center justify-center gap-2 bg-white/10 backdrop-blur border border-white/30 hover:bg-white/20 text-white font-semibold px-6 py-4 rounded-full text-base transition-all"
                >
                  See Live Campaigns
                </a>
              </div>
              <p className="text-xs text-white/40 mt-4">No credit card required. Setup takes 5 minutes.</p>
            </div>

            {/* Stats card */}
            <div className="flex-shrink-0 bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-6 w-full max-w-xs">
              <p className="text-white/60 text-xs font-semibold uppercase tracking-wider mb-4">Platform Stats</p>
              {[
                { label: 'Avg days to funded offer', value: '7' },
                { label: 'Avg cash offer vs asking', value: '98%' },
                { label: 'Active campaigns right now', value: '14' },
                { label: 'Offer acceptance window', value: '24 hrs' },
              ].map(({ label, value }) => (
                <div key={label} className="flex items-center justify-between py-2.5 border-b border-white/10 last:border-0">
                  <span className="text-sm text-white/70">{label}</span>
                  <span className="text-base font-bold text-white">{value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-gray-900">How FAM works for dealers</h2>
            <p className="text-gray-500 mt-2">Three simple steps from listing to cash in hand</p>
          </div>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              {
                step: '1',
                title: 'List your inventory',
                desc: 'Provide your website URL — we scrape and sync your inventory automatically. No manual uploads required.',
                icon: Globe,
                color: 'bg-amber-100 text-amber-600',
              },
              {
                step: '2',
                title: 'Buyers get funded by their FAM',
                desc: 'Customers launch campaigns on FAM. Their friends, family, and investors back them. FAM underwrites the full purchase amount.',
                icon: Users,
                color: 'bg-rose-100 text-rose-500',
              },
              {
                step: '3',
                title: 'You receive a standing cash offer',
                desc: 'When a creator\'s campaign funds, FAM generates a standing offer. You have 24 hours to accept — then submit title and receive full cash within 5 business days.',
                icon: DollarSign,
                color: 'bg-emerald-100 text-emerald-600',
              },
            ].map(({ step, title, desc, icon: Icon, color }) => (
              <div key={step} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 relative">
                <div className="absolute -top-3 -left-3 w-8 h-8 rounded-full bg-indigo-600 text-white text-sm font-bold flex items-center justify-center shadow">
                  {step}
                </div>
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 ${color}`}>
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-extrabold text-gray-900 mb-3">
                Everything dealers need,<br />nothing they don't
              </h2>
              <p className="text-gray-500 mb-8 leading-relaxed">
                FAM replaces complex finance arrangements with a simple cash purchase model.
                No dealership fees, no floor plan risk, no waiting on loan approvals.
              </p>
              <div className="space-y-4">
                {[
                  { title: 'Full cash at closing', desc: 'FAM buys the vehicle outright. No financing contingencies.' },
                  { title: 'Free inventory exposure', desc: 'Your vehicles reach FAM\'s active buyer network automatically.' },
                  { title: '24-hour standing offer window', desc: 'Once a creator\'s campaign funds, you have 24 hours to accept. No drawn-out negotiations.' },
                  { title: 'Branding & QR code toolkit', desc: 'Print-ready signage, embeddable inventory widget, and shareable links.' },
                  { title: 'Inventory boost available', desc: 'Optional paid placement to appear at the top of buyer vehicle lists.' },
                ].map(({ title, desc }) => (
                  <div key={title} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-gray-800 text-sm">{title}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Offer illustration */}
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 border border-indigo-100">
              <p className="text-xs font-semibold text-indigo-400 uppercase tracking-wider mb-4">Sample Standing Offer</p>
              <div className="bg-white rounded-xl p-5 shadow-sm mb-4">
                <div className="flex items-center gap-3 mb-4">
                  <img
                    src="https://images.unsplash.com/photo-1621007947382-bb3c3994e3fb?w=80&q=80"
                    className="w-16 h-12 object-cover rounded-lg"
                    alt="vehicle"
                  />
                  <div>
                    <p className="font-bold text-gray-900 text-sm">2022 Toyota Camry</p>
                    <p className="text-xs text-gray-500">14,200 mi · Your Dealership</p>
                  </div>
                </div>
                <div className="flex items-center justify-between py-2 border-t border-gray-100">
                  <span className="text-sm text-gray-500">Your asking price</span>
                  <span className="font-semibold text-gray-800">$28,400</span>
                </div>
                <div className="flex items-center justify-between py-2 border-t border-gray-100">
                  <span className="text-sm text-gray-500">FAM cash offer</span>
                  <span className="font-bold text-emerald-600 text-lg">$28,400</span>
                </div>
                <div className="mt-3 flex items-center gap-2 bg-emerald-50 rounded-lg px-3 py-2">
                  <Shield className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  <span className="text-xs text-emerald-700 font-medium">Full asking price · no negotiation</span>
                </div>
              </div>
              <p className="text-xs text-center text-indigo-400">24-hr acceptance window · Funds within 5 business days of title transfer</p>
            </div>
          </div>
        </div>
      </section>

      {/* Social proof */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              {
                quote: 'Listed my lot on Monday, had a funded offer by Wednesday. Easiest deal I\'ve done all quarter.',
                name: 'Marcus T.',
                dealership: 'Eastside Motors',
              },
              {
                quote: 'The QR code sign we printed brings people into our showroom every week asking about FAM financing.',
                name: 'Sandra L.',
                dealership: 'Lakeside Auto Group',
              },
              {
                quote: 'No more waiting on banks. FAM pays cash and the whole process is cleaner than any lender I\'ve worked with.',
                name: 'David R.',
                dealership: 'Freedom Ford Pre-Owned',
              },
            ].map(({ quote, name, dealership }) => (
              <div key={name} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                <div className="flex gap-0.5 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="text-sm text-gray-600 italic mb-4">"{quote}"</p>
                <div>
                  <p className="text-sm font-bold text-gray-900">{name}</p>
                  <p className="text-xs text-gray-400">{dealership}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20">
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <h2 className="text-3xl font-extrabold text-gray-900 mb-3">Simple pricing</h2>
          <p className="text-gray-500 mb-10">Start free. Upgrade only if you want faster placement.</p>
          <div className="grid sm:grid-cols-2 gap-6 text-left">
            {[
              {
                tier: 'Free',
                price: '$0',
                sub: 'No credit card',
                features: [
                  'Full inventory listing',
                  'Standing offer on every vehicle',
                  'QR code & branding toolkit',
                  'Embeddable inventory widget',
                  'Unlimited vehicles',
                ],
                cta: 'Get Started Free',
                highlight: false,
              },
              {
                tier: 'Inventory Boost',
                price: '$149',
                sub: 'per month',
                features: [
                  'Everything in Free',
                  'Top placement in buyer vehicle lists',
                  'Featured dealer badge',
                  'Priority in FAM campaign matching',
                  'Monthly performance report',
                ],
                cta: 'Start with Boost',
                highlight: true,
              },
            ].map(({ tier, price, sub, features, cta, highlight }) => (
              <div key={tier} className={`rounded-2xl p-7 border-2 ${highlight ? 'border-rose-400 shadow-lg shadow-rose-100' : 'border-gray-200'}`}>
                {highlight && (
                  <span className="inline-block bg-gradient-to-r from-rose-500 to-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full mb-3">Most popular</span>
                )}
                <p className="font-bold text-gray-900 text-lg">{tier}</p>
                <div className="flex items-end gap-1 mt-1 mb-5">
                  <span className="text-3xl font-extrabold text-gray-900">{price}</span>
                  <span className="text-gray-400 text-sm mb-1">{sub}</span>
                </div>
                <ul className="space-y-2.5 mb-7">
                  {features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm text-gray-600">
                      <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={onGetStarted}
                  className={`w-full py-3 rounded-full font-bold text-sm transition-all ${
                    highlight
                      ? 'bg-gradient-to-r from-rose-500 to-amber-500 hover:from-rose-400 hover:to-amber-400 text-white'
                      : 'border-2 border-rose-200 text-rose-600 hover:bg-rose-50'
                  }`}
                >
                  {cta}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-20 overflow-hidden text-white text-center">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1605893477799-b99e3b8b93fe?w=1600&q=80')" }} />
        <div className="absolute inset-0 bg-gray-950/80" />
        <div className="relative container mx-auto px-4 max-w-2xl">
          <TrendingUp className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <h2 className="text-3xl font-extrabold mb-4">Ready to list your inventory?</h2>
          <p className="text-white/70 mb-8">
            Join dealers already receiving cash offers through FAM. Setup takes 5 minutes.
          </p>
          <button
            onClick={onGetStarted}
            className="bg-gradient-to-r from-rose-500 to-amber-500 hover:from-rose-400 hover:to-amber-400 text-white font-bold px-10 py-4 rounded-full text-base shadow-lg transition-all"
          >
            List My Inventory Free →
          </button>
          <p className="text-white/40 text-xs mt-4">No credit card · No commitment · Cancel anytime</p>
        </div>
      </section>

    </div>
  );
}
